// frontend/src/modules/integrations/components/QuickBooksFinanceSection.tsx
//
// "Your financials" — live P&L + cash-flow summary pulled from QuickBooks
// for the dashboard home. Renders NOTHING unless the user holds
// invoices.view AND the tenant has QuickBooks connected, so the dashboard
// stays clean for everyone else. All figures come straight from QBO's
// Reports API — no fabricated numbers; loading shows em-dashes and fetch
// failures show an honest message (HomePage house style).

import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useApi } from '../../../design-system/useApi';
import { usePermission } from '../../../core/auth/usePermission';
import { formatMoney } from '../../../design-system/format';
import { BentoGrid, GlassCard, Eyebrow } from '../../../design-system/pds';
import type { QuickBooksStatus, QuickBooksPnl, QuickBooksCashFlow } from '../types';
import { periodRange, PERIOD_LABELS, FinancePeriod } from '../period';

const PERIODS: FinancePeriod[] = ['this_month', 'last_month', 'ytd'];

export function QuickBooksFinanceSection(): JSX.Element | null {
  const canView = usePermission('invoices.view');
  const statusQ = useApi<QuickBooksStatus>(canView ? '/api/integrations/quickbooks/status' : null);
  const connected = canView && statusQ.data?.connected === true;

  const [period, setPeriod] = React.useState<FinancePeriod>('this_month');
  const { start, end } = React.useMemo(() => periodRange(period, new Date()), [period]);

  const pnlQ = useApi<{ report: QuickBooksPnl }>(
    connected ? `/api/integrations/quickbooks/reports/profit-and-loss?start_date=${start}&end_date=${end}` : null,
  );
  const cashQ = useApi<{ report: QuickBooksCashFlow }>(
    connected ? `/api/integrations/quickbooks/reports/cash-flow?start_date=${start}&end_date=${end}` : null,
  );

  // Fail closed to hidden: no permission, status unknown/errored, or not connected.
  if (!connected) return null;

  const pnl = pnlQ.data?.report ?? null;
  const cash = cashQ.data?.report ?? null;
  const loading = (pnlQ.loading && !pnl) || (cashQ.loading && !cash);
  const errored = !!(pnlQ.error || cashQ.error);
  const currency = pnl?.currency ?? cash?.currency ?? 'USD';

  const money = (v: number | null | undefined) =>
    loading || errored || v == null ? '—' : formatMoney(v, currency);

  return (
    <BentoGrid cols={12} className="mb-6">
      <GlassCard className="col-span-12" padding="p-7" motionDelay={0.05}>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <Eyebrow className="mb-2">QuickBooks · your financials</Eyebrow>
            <p className="text-title-lg text-ink dark:text-white">
              Profit &amp; loss · {PERIOD_LABELS[period].toLowerCase()}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                aria-pressed={period === p}
                className={[
                  'text-[12px] font-bold px-3 py-1.5 rounded-full transition-colors',
                  period === p
                    ? 'bg-coral text-white'
                    : 'bg-ink/[0.05] text-ink/60 hover:bg-ink/[0.1] dark:bg-white/[0.07] dark:text-white/60 dark:hover:bg-white/[0.12]',
                ].join(' ')}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        {errored ? (
          <p className="text-[13px] text-ink/60 dark:text-white/60">
            QuickBooks figures couldn’t load — the connection may need attention.{' '}
            <Link to="/dashboard/settings/integrations" className="font-bold text-coral hover:underline underline-offset-4">
              Open integration settings
            </Link>
          </p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
            <FinanceStat label="Income" value={money(pnl?.total_income)} />
            <FinanceStat label="Expenses" value={money(pnl == null ? null : pnl.total_expenses + pnl.total_cogs)} />
            <FinanceStat
              label="Net income"
              value={money(pnl?.net_income)}
              tone={pnl == null ? 'default' : pnl.net_income >= 0 ? 'positive' : 'negative'}
            />
            <FinanceStat
              label="Net cash change"
              value={money(cash?.net_cash_change)}
              sub={cash?.ending_cash != null ? `${formatMoney(cash.ending_cash, currency)} cash on hand` : undefined}
              tone={cash == null ? 'default' : cash.net_cash_change >= 0 ? 'positive' : 'negative'}
            />
          </div>
        )}

        <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[12px] text-ink/45 dark:text-white/45">
            {loading || errored ? 'Live from QuickBooks' : `${start} → ${end} · live from QuickBooks`}
          </p>
          <Link
            to="/dashboard/settings/integrations"
            className="inline-flex items-center gap-1 text-[12.5px] font-bold text-coral hover:underline underline-offset-4"
          >
            Manage connection <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </GlassCard>
    </BentoGrid>
  );
}

function FinanceStat({
  label, value, sub, tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'positive' | 'negative';
}) {
  return (
    <div>
      <Eyebrow className="mb-2">{label}</Eyebrow>
      <p
        className={[
          'font-bold text-stat-md leading-none tabular-nums tracking-[-0.025em]',
          tone === 'positive' ? 'text-emerald-600 dark:text-emerald-300'
            : tone === 'negative' ? 'text-rose-600 dark:text-rose-300'
            : 'text-ink dark:text-white',
        ].join(' ')}
      >
        {value}
      </p>
      {sub && <p className="text-[12px] text-ink/55 dark:text-white/55 mt-2 truncate">{sub}</p>}
    </div>
  );
}
